import { FunctionComponent, useState, useEffect, Dispatch, SetStateAction } from 'react'
import {
  Box,
  Select as MuiSelect,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  Switch,
  Checkbox,
  makeStyles,
  MenuItem
} from '@material-ui/core'
import { Help, KeyboardArrowDown } from '@material-ui/icons'
import { BackendUser, OnboardingQuestion, BackendPartner, PartnerType } from 'common/types'
import { useFileUpload } from 'hooks/use-file-upload/useFileUpload'
import { useScreenSizes } from 'hooks/use-screen-sizes/useScreenSizes'
//@ts-ignore
import styled, { useTheme } from 'styled-components'
import { Location } from '../../../onboarding/components/onboarding-questions/Location'
import { industries, expertise, experience, age, gender, yearsOfExperience, countryCode } from 'common/select-options'
import { AddAvatar } from './AddAvatar'
import validator from 'validator'
import { companySize } from 'common/select-options/companySize'
import { AddBanner } from '../edit-partner-profile/AddBanner'
import { AddLogo } from '../edit-partner-profile/AddLogo'
import { darken } from 'polished'
import { Select } from 'common/molecules/Select/Select'
import { InputFieldGroup } from 'common/molecules/InputFieldGroup/InputFieldGroup'

const useStyles = makeStyles(() => ({
  root: {
    '& .appear-item': {
      display: 'none'
    },
    '&:hover .appear-item': {
      display: 'block',
      color: `${props => props.theme.colors.supplementary1}`,
      padding: '0 1em 0 1em',
      margin: '-.7em 0em 0em .1em',
      borderRadius: '16px',
      position: 'absolute'
    }
  },
  select: {
    width: '300px',
    minWidth: '0px !important'
  },
  selectClassification: {
    width: '300px',
    minWidth: '0px !important',
    top: '61vh !important',
    left: '43% !important'
  },
  helperText: {
    textAlign: 'end'
  },
  textField: {
    '& p': {
      color: 'red'
    }
  }
}))

const validators = [
  {
    name: 'email',
    validate: email => !email || validator.isEmail(email),
    helperText: 'Enter valid email'
  },
  {
    name: 'phone',
    validate: phone => !phone || (validator.isMobilePhone(phone, 'any') && phone?.length >= 10),
    helperText: 'Enter valid phone number'
  }
]

const FadeIn = ({ duration = 300, delay = 0, children, ...delegated }) => {
  return (
    <Box
      {...delegated}
      style={{
        ...(delegated.style || {}),
        animationDuration: duration + 'ms',
        animationDelay: delay + 'ms'
      }}
    >
      {children}
    </Box>
  )
}

const StyledBox = styled(Box)`
  color: ${props => props.theme.colors.accent1};
  text-align: right;
`

export const StyledTextField = styled(TextField)`
  && .MuiInputBase-root {
    background-color: ${props => props.theme.colors.bgPrimary};
  }
  & .MuiInputBase-root.Mui-disabled {
    color: ${props => darken(0.35, props.theme.colors.accent)} !important;
  }
`
export const StyledSelect = styled(MuiSelect)`
  && {
    background-color: ${props => props.theme.colors.bgPrimary};
  }
`
interface EditProfilePageProps {
  user: BackendUser
  company?: BackendPartner
  page: number
  editProfileQuestions: OnboardingQuestion[]
  setProfileQuestions: Function
  pageQuestions: OnboardingQuestion[]
  setUpdateEnabled: Function
  updateButtonEnabled: boolean
  setUpdateButtonEnabled: Dispatch<SetStateAction<boolean>>
  fileName: string
  setFileName: Dispatch<SetStateAction<string>>
  previewImage: string
  setPreviewImage: Dispatch<SetStateAction<string>>
  previewBanner: string
  setPreviewBanner: Dispatch<SetStateAction<string>>
  questionAnswers: OnboardingQuestion[]
  setAnswers: Function
  requiredPageQuestionsAnswered: Function
  checkUrlValidity: Function
  isValidUrl: boolean
  setIsValidUrl: Dispatch<SetStateAction<boolean>>
  getQuestionIndex: Function
  avatarUpdated: boolean
  setAvatarUpdated: Dispatch<SetStateAction<boolean>>
  eventProfile?: boolean
  view?: string
  isDuplicateCompanyName?: Function
  companyNameError?: boolean
  setCompanyNameError?: Dispatch<SetStateAction<boolean>>
}

export const EditProfilePage: FunctionComponent<EditProfilePageProps> = ({
  user,
  page,
  company,
  editProfileQuestions,
  setProfileQuestions,
  pageQuestions,
  setUpdateEnabled,
  updateButtonEnabled,
  setUpdateButtonEnabled,
  fileName,
  setFileName,
  previewImage,
  setPreviewImage,
  previewBanner,
  setPreviewBanner,
  requiredPageQuestionsAnswered,
  checkUrlValidity,
  isValidUrl,
  setIsValidUrl,
  getQuestionIndex,
  avatarUpdated,
  setAvatarUpdated,
  eventProfile,
  view,
  isDuplicateCompanyName,
  companyNameError,
  setCompanyNameError
}) => {
  const theme = useTheme()
  const classes = useStyles()
  const { isDevice } = useScreenSizes()
  const { getProcessedFile } = useFileUpload()
  const [phoneCode, setPhoneCode] = useState<string>()
  const filteredQuestions = pageQuestions?.filter(question => question?.question !== 'Area of Expertise')

  const locationAnswer = editProfileQuestions?.find(x => x.question === 'Location')?.userAnswer
  const [location, setLocation] = useState([
    `${locationAnswer?.split(',')[0]}`,
    `${locationAnswer?.split(',')[1]}`,
    `${locationAnswer?.split(',')[2]}`
  ])

  const getComplexAnswer = (index: number, complexQuestion: string) => {
    const question = editProfileQuestions[index]
    const answerArray = question?.userAnswer ? JSON.parse(question?.userAnswer) : []
    const answer = answerArray.find(x => x?.inQuestion === complexQuestion)
    return answer?.inAnswer
  }

  const getSingleRadioAnswer = (index: number, value: string) => {
    const question = editProfileQuestions[index]
    return question?.userAnswer === value
  }

  const isCompany = company ? true : false

  const getPartnerProfileQuestions = (question: string) => {
    if (company?.type == PartnerType.Community) {
      switch (question) {
        case 'First Name':
          return 'First Name'
        case 'Last Name':
          return 'Last Name'
        case 'Headline':
          return 'Headline'
        case 'Company Name':
          return 'Community Name'
        case 'Company Email Address':
          return 'Community Email Address'
        case 'Company Phone Number':
          return 'Community Phone Number'
        case 'Bio Summary':
          return 'Profile Description Summary'
        case 'Company Website':
          return 'Community Website'
        case 'Company Size Classification':
          return 'Community Size Classification'
        case 'Website':
          return 'Website'
      }
    } else if (company?.type == PartnerType.Corporate) {
      switch (question) {
        case 'First Name':
          return 'First Name'
        case 'Last Name':
          return 'Last Name'
        case 'Headline':
          return 'Headline'
        case 'Company Name':
          return 'Company Name'
        case 'Company Email Address':
          return 'Company Email Address'
        case 'Company Phone Number':
          return 'Company Phone Number'
        case 'Bio Summary':
          return 'Profile Description Summary'
        case 'Company Website':
          return 'Company Website'
        case 'Company Size Classification':
          return 'Company Size Classification'
        case 'Website':
          return 'Website'
      }
    }
  }

  const handleChange = (
    index: number,
    e: any,
    isComplex?: boolean,
    complexQuestion?: string,
    isLocation?: boolean,
    isSwitch?: boolean
  ) => {
    const question = editProfileQuestions[index]
    if (question?.question === 'Company Name') {
      if (companyNameError) setCompanyNameError(false)
    }

    if (isComplex) {
      const question = editProfileQuestions[index]
      const answerArray = question?.userAnswer ? JSON.parse(question?.userAnswer) : []
      let answer = answerArray.find(x => x?.inQuestion === complexQuestion)
      if (!answer) {
        answer = {}
        answer.inQuestion = complexQuestion
        answer.inAnswer = e.target.checked ? 'yes' : 'no'
        answerArray.push(answer)
      } else {
        answer.inAnswer = e.target.checked ? 'yes' : 'no'
        answerArray[answerArray.indexOf(answer)] = answer
      }
      question.userAnswer = JSON.stringify(answerArray)
      editProfileQuestions[index] = question
    } else if (isLocation) {
      const question = editProfileQuestions[index]
      question.userAnswer = e.toString()
      editProfileQuestions[index] = question
    } else if (isSwitch) {
      const question = editProfileQuestions[index]
      question.userAnswer = e.target.checked === true ? 'true' : 'false'
      editProfileQuestions[index] = question
    } else if (question?.question === 'Logo' || question?.question === 'Banner' || question?.question === 'avatar') {
      if (e.target.value.includes('jpeg') || e.target.value.includes('jpg') || e.target.value.includes('png')) {
        question.userAnswer = e.target.value
        editProfileQuestions[index] = question
      }
    } else {
      const question = editProfileQuestions[index]
      if (question.question === 'Website' || question.question === 'Company Website') {
        let value = e.target.value
        question.userAnswer = value
        if (value.length > 0) {
          const validity = validator.isURL(value)
          setIsValidUrl(validity)
        } else if (value.length === 0) {
          setIsValidUrl(true)
        }
      }

      question.userAnswer = e.target.value
      editProfileQuestions[index] = question
    }

    setUpdateButtonEnabled(true)
    setProfileQuestions([...editProfileQuestions])
  }

  const getOptions = (field: string) => {
    switch (field) {
      case 'industry':
        return industries
      case 'expertise':
        return expertise
      case 'industry_years':
        return experience
      case 'experience_level':
        return yearsOfExperience
      case 'age_range':
        return age
      case 'gender':
        return gender
      case 'company_size':
        return companySize
      case 'country_code':
        return countryCode
    }
  }

  useEffect(() => {
    requiredPageQuestionsAnswered(
      view === 'partner' ? filteredQuestions : pageQuestions,
      editProfileQuestions,
      setUpdateEnabled,
      eventProfile
    )
    if (page === 2) {
      if (
        !checkUrlValidity(editProfileQuestions[getQuestionIndex('Website', editProfileQuestions)]?.userAnswer) &&
        editProfileQuestions[getQuestionIndex('Website', editProfileQuestions)]?.userAnswer
      ) {
        setUpdateEnabled(false)
        setUpdateButtonEnabled(false)
      }
      if (
        !checkUrlValidity(
          editProfileQuestions[getQuestionIndex('Company Website', editProfileQuestions)]?.userAnswer
        ) &&
        editProfileQuestions[getQuestionIndex('Company Website', editProfileQuestions)]?.userAnswer
      ) {
        setUpdateEnabled(false)
        setUpdateButtonEnabled(false)
      }
    }
    if (page === 1) {
      const regex = /^[ A-Za-z0-9!"#$%&'()*+,-./:;<=>?@[\]^_`{|}~]*$/

      if (!user?.partnerId) {
        const validateFirstName =
          editProfileQuestions
            ?.find(x => x.question === 'First Name')
            ?.userAnswer?.charAt(0)
            ?.match(regex) && editProfileQuestions.find(x => x.question === 'First Name')?.userAnswer.charAt(0) != ' '
            ? true
            : false

        const validateLastName =
          editProfileQuestions
            ?.find(x => x.question === 'Last Name')
            ?.userAnswer?.charAt(0)
            ?.match(regex) && editProfileQuestions.find(x => x.question === 'Last Name')?.userAnswer.charAt(0) != ' '
            ? true
            : false

        const checkInputValidation = validateFirstName && validateLastName
        if (!checkInputValidation) {
          setUpdateEnabled(false)
          setUpdateButtonEnabled(false)
        }
      }
    }

    const phoneNumber = editProfileQuestions[getQuestionIndex('Company Phone Number', editProfileQuestions)]?.userAnswer
    const phoneNumberCode = phoneNumber?.split(' ')?.[0]
    setPhoneCode(phoneNumberCode)
  }, [editProfileQuestions, page])

  useEffect(() => {
    setUpdateButtonEnabled(updateButtonEnabled)
  }, [updateButtonEnabled])
  // The width of the text and select inputs is defined in the select component. It has a width of 37em on non-Device
  const getPageComponents = () => {
    return (view === 'partner' ? filteredQuestions : pageQuestions)?.map(question => {
      switch (question.displayType) {
        case 'file': {
          return (
            <>
              {question?.question === 'avatar' && (
                <AddAvatar
                  questionIndex={editProfileQuestions?.indexOf(question)}
                  handleChange={handleChange}
                  editProfileQuestions={editProfileQuestions}
                  setProfileQuestions={setProfileQuestions}
                  getProcessedFile={getProcessedFile}
                  user={user}
                  fileName={fileName}
                  setFileName={setFileName}
                  previewImage={previewImage}
                  setPreviewImage={setPreviewImage}
                  avatarUpdated={avatarUpdated}
                  setAvatarUpdated={setAvatarUpdated}
                />
              )}
              {question?.question === 'Banner' && (
                <AddBanner
                  company={company}
                  questionIndex={editProfileQuestions?.indexOf(question)}
                  handleChange={handleChange}
                  editProfileQuestions={editProfileQuestions}
                  setProfileQuestions={setProfileQuestions}
                  getProcessedFile={getProcessedFile}
                  fileName={fileName}
                  setFileName={setFileName}
                  previewImage={previewBanner}
                  setPreviewImage={setPreviewBanner}
                  avatarUpdated={avatarUpdated}
                  setAvatarUpdated={setAvatarUpdated}
                />
              )}
              {question?.question === 'Logo' && (
                <AddLogo
                  company={company}
                  questionIndex={editProfileQuestions?.indexOf(question)}
                  handleChange={handleChange}
                  editProfileQuestions={editProfileQuestions}
                  setProfileQuestions={setProfileQuestions}
                  getProcessedFile={getProcessedFile}
                  fileName={fileName}
                  setFileName={setFileName}
                  previewImage={previewImage}
                  setPreviewImage={setPreviewImage}
                  avatarUpdated={avatarUpdated}
                  setAvatarUpdated={setAvatarUpdated}
                />
              )}
            </>
          )
        }
        case 'text': {
          var validate = validators?.find(v => question?.question.toLocaleLowerCase().includes(v.name))
          var isError =
            validate &&
            !validate?.validate(
              editProfileQuestions[getQuestionIndex(question.question, editProfileQuestions)]?.userAnswer
            )
          var isPhoneNumber = question?.question.toLocaleLowerCase() === 'company phone number'
          var wordCountlength =
            editProfileQuestions[getQuestionIndex(question.question, editProfileQuestions)]?.userAnswer?.length ?? 0
          const wordCountMaxLength = 255

          const inputErrorState =
            wordCountlength >= wordCountMaxLength ||
            isError ||
            ((question?.question === 'Website' || question?.question === 'Company Website') && !isValidUrl)

          return (
            <Box padding="0.5em 0">
              {isCompany && (
                <Typography variant="body2" component="p" align="left">
                  <Box display="flex" fontWeight="bold">
                    {getPartnerProfileQuestions(question?.question)}
                    {question.required && (
                      <Box color={theme.colors.error} component="span" padding="0 0.5em 0 0">
                        <sup>*</sup>
                      </Box>
                    )}
                  </Box>
                </Typography>
              )}

              {!isCompany && (
                <Typography variant="body2" component="p" align="left">
                  <Box display="flex" fontWeight="bold">
                    {question?.question}
                    {question.required && (
                      <Box color={theme.colors.error} component="span" padding="0 0.5em 0 0">
                        <sup>*</sup>
                      </Box>
                    )}
                  </Box>
                </Typography>
              )}

              <Box width="100%" display="flex">
                {isPhoneNumber && (
                  <Box width="40%" pr="0.5em">
                    <Select
                      width="100%"
                      theme={theme}
                      menuItemList={[{ value: '', label: '' }, ...countryCode]}
                      value={phoneCode}
                      MenuProps={{ classes: { paper: classes.select } }}
                      defaultValue={'CA +1'}
                      disableUnderline
                      fullWidth
                      onChange={(e: any) => {
                        setPhoneCode(e.target.value)
                      }}
                    ></Select>
                  </Box>
                )}
                {isDevice && (
                  <InputFieldGroup
                    charLimit={255}
                    showCounter={wordCountlength >= wordCountMaxLength}
                    error={inputErrorState}
                    FormHelperTextProps={{
                      className: classes.helperText
                    }}
                    className={classes.textField}
                    inputProps={{ maxLength: wordCountMaxLength ?? undefined }}
                    value={editProfileQuestions[getQuestionIndex(question.question, editProfileQuestions)]?.userAnswer}
                    size="small"
                    fullWidth
                    onBlur={async e => {
                      if (question.question === 'Company Name' && window.location.pathname.includes('/company')) {
                        let isDuplicate
                        if (e.target.value.length) isDuplicate = await isDuplicateCompanyName(e.target.value)
                        if (company?.name?.toLowerCase() !== e.target.value?.toLowerCase() && isDuplicate) {
                          setCompanyNameError(true)
                          return
                        }
                      }
                    }}
                    onChange={(e: any) => {
                      handleChange(getQuestionIndex(question.question, editProfileQuestions), e)
                    }}
                  />
                )}

                {!isDevice && (
                  <InputFieldGroup
                    charLimit={255}
                    showCounter={wordCountlength >= wordCountMaxLength}
                    error={inputErrorState}
                    FormHelperTextProps={{
                      className: classes.helperText
                    }}
                    className={classes.textField}
                    inputProps={{ maxLength: wordCountMaxLength ?? undefined }}
                    value={editProfileQuestions[getQuestionIndex(question.question, editProfileQuestions)]?.userAnswer}
                    fullWidth
                    onBlur={async e => {
                      if (question.question === 'Company Name' && window.location.pathname.includes('/company')) {
                        let isDuplicate
                        if (e.target.value?.length) isDuplicate = await isDuplicateCompanyName(e.target.value)
                        if (company?.name?.toLowerCase() !== e.target.value?.toLowerCase() && isDuplicate) {
                          setCompanyNameError(true)
                          return
                        }
                      }
                    }}
                    onChange={(e: any) => {
                      handleChange(getQuestionIndex(question.question, editProfileQuestions), e)
                    }}
                  />
                )}
              </Box>
              {question?.question === 'Company Name' && companyNameError && (
                <Typography variant="body2">
                  <Box color={theme.colors.error}> Company name already exists</Box>
                </Typography>
              )}
              {(question?.question === 'Website' || question?.question === 'Company Website') && !isValidUrl && (
                <Box padding="0.5em 0">
                  <Typography variant="body2">
                    <Box lineHeight="1em" color={theme.colors.error} textAlign="justify">
                      Invalid Url.
                    </Box>
                  </Typography>
                </Box>
              )}
            </Box>
          )
        }
        case 'select': {
          return (
            <>
              {question.options[0] === 'location' && (
                <Location
                  location={location}
                  setLocation={setLocation}
                  questionIndex={editProfileQuestions?.indexOf(question)}
                  handleChange={handleChange}
                  editProfileQuestions={editProfileQuestions}
                  getQuestionIndex={getQuestionIndex}
                />
              )}
              {question.options[0] !== 'location' && (
                <Box padding="0.5em 0" width={isDevice ? '100%' : '46em'}>
                  {question.question === 'Industry' && view === 'member' && (
                    <Typography variant="h6">
                      <Box padding="0.5em 0" fontWeight="bold">
                        Share your experience
                      </Box>
                    </Typography>
                  )}
                  <Box>
                    {question.question !== 'Gender Identity' && (
                      <Typography variant="body2" component="p" align="left" style={{ fontWeight: 'bold' }}>
                        {question?.question === 'Company Size Classification' && isCompany
                          ? getPartnerProfileQuestions(question?.question)
                          : question?.question}
                        {question.required && (
                          <Box color={theme.colors.error} component="span">
                            <sup>*</sup>
                          </Box>
                        )}
                        <Box display="inline-flex" component="div" className={classes.root}>
                          {question?.question.includes('Years of experience') ? (
                            <Help style={{ fontSize: '.7em', cursor: 'pointer' }} />
                          ) : (
                            ''
                          )}
                          {question?.question.includes('Years of experience') ? (
                            <Typography variant="caption">
                              <Box component="span" className="appear-item" bgcolor={theme.colors.supplementary1}>
                                Your current role
                              </Box>
                            </Typography>
                          ) : (
                            ''
                          )}
                        </Box>
                      </Typography>
                    )}
                    {question.question === 'Gender Identity' && (
                      <Typography variant="body2" component="p" align="left" style={{ fontWeight: 'bold' }}>
                        Gender Identity{' '}
                        <Typography component="span" variant="body2" style={{ fontWeight: 'bold' }}>
                          (Internal use only)
                        </Typography>
                      </Typography>
                    )}
                  </Box>
                  {isDevice && (
                    <Select
                      width="100%"
                      theme={theme}
                      menuItemList={[...getOptions(question?.options[0])]}
                      value={
                        editProfileQuestions[getQuestionIndex(question.question, editProfileQuestions)]?.userAnswer ||
                        ''
                      }
                      MenuProps={{ classes: { paper: classes.select } }}
                      fullWidth
                      style={{
                        height: '2.67em'
                      }}
                      disableUnderline
                      onChange={(e: any) => {
                        handleChange(getQuestionIndex(question.question, editProfileQuestions), e)
                      }}
                    >
                      {getOptions(question?.options[0])?.map((p, i) => {
                        return (
                          <MenuItem value={p.value} key={i}>
                            {p.label}
                          </MenuItem>
                        )
                      })}
                    </Select>
                  )}

                  {!isDevice && (
                    <Select
                      width="100%"
                      theme={theme}
                      menuItemList={[...getOptions(question?.options[0])]}
                      value={
                        editProfileQuestions[getQuestionIndex(question.question, editProfileQuestions)]?.userAnswer ||
                        ''
                      }
                      MenuProps={{
                        classes: {
                          paper:
                            question?.question === 'Company Size Classification'
                              ? classes.selectClassification
                              : classes.select
                        }
                      }}
                      fullWidth
                      disableUnderline
                      onChange={(e: any) => {
                        handleChange(getQuestionIndex(question.question, editProfileQuestions), e)
                      }}
                    >
                      {getOptions(question?.options[0])?.map((p, i) => {
                        return (
                          <MenuItem value={p.value} key={i}>
                            {p.label}
                          </MenuItem>
                        )
                      })}
                    </Select>
                  )}
                </Box>
              )}
            </>
          )
        }
        case 'radio-single': {
          return (
            <>
              <Box></Box>
              <Box>
                <Box display="flex" flexDirection="column" alignItems="flex-start">
                  <RadioGroup>
                    {question.options?.map(x => {
                      return (
                        <Box display="flex" padding="0.75em 0">
                          <Radio
                            value={x.split(':')[0]}
                            checked={getSingleRadioAnswer(editProfileQuestions?.indexOf(question), x.split(':')[0])}
                            onChange={(e: any) => {
                              handleChange(editProfileQuestions?.indexOf(question), e)
                            }}
                            name={`${x.split(':')[0]}-black-community`}
                            inputProps={{ 'aria-label': `${x.split(':')[0]}, ${x.split(':')[0]}` }}
                          />

                          <Box display="flex" flexDirection="column">
                            <Typography variant="h5">{x.split(':')[0]}</Typography>
                          </Box>
                        </Box>
                      )
                    })}
                  </RadioGroup>
                </Box>
              </Box>
            </>
          )
        }
        case 'complex': {
          return (
            <>
              <Box>
                <Typography component="p" align="left">
                  {question.required && (
                    <Box color={theme.colors.error} component="span">
                      <sup>*</sup>
                    </Box>
                  )}
                </Typography>
              </Box>
              <Box display="flex" flexDirection={isDevice ? 'column' : 'row'} width="100%">
                <Box display="flex" flexDirection="column" width={isDevice ? '100%' : '50em'}>
                  {question.options?.map((x, i) => {
                    return (
                      <Box display="flex" flexDirection="row">
                        <Checkbox
                          style={{
                            color: theme.colors.primary
                          }}
                          checked={getComplexAnswer(editProfileQuestions?.indexOf(question), x.split(':')[1]) === 'yes'}
                          onChange={(e: any) => {
                            handleChange(editProfileQuestions?.indexOf(question), e, true, x.split(':')[1])
                          }}
                        />
                        <Box>
                          <Typography
                            variant={isDevice ? 'caption' : 'h6'}
                            style={{ lineHeight: isDevice ? '3em' : '2em' }}
                          >
                            {x.split(':')[1]}
                          </Typography>
                        </Box>
                      </Box>
                    )
                  })}
                </Box>
              </Box>
            </>
          )
        }
        case 'switch': {
          return (
            <>
              {editProfileQuestions[getQuestionIndex(question.question, editProfileQuestions)]?.question ===
                'Show Goals' && (
                <Box display="flex" justifyContent="space-between" mb="1em">
                  <Typography variant={isDevice ? 'subtitle2' : 'h6'} component="span" style={{ alignSelf: 'center' }}>
                    <Box fontWeight="bold">Main Goal(s) for joining Obsidi</Box>
                  </Typography>
                  <Box display="flex" justifyContent={isDevice ? 'center' : ''}>
                    <Switch
                      color="primary"
                      size="small"
                      onChange={(e: any) => {
                        handleChange(
                          getQuestionIndex(question.question, editProfileQuestions),
                          e,
                          false,
                          '',
                          false,
                          true
                        )
                      }}
                      checked={
                        editProfileQuestions[getQuestionIndex(question.question, editProfileQuestions)]?.userAnswer ===
                        'true'
                          ? true
                          : false
                      }
                    />
                    <Typography
                      variant={isDevice ? 'subtitle2' : 'subtitle1'}
                      component="span"
                      style={{ alignSelf: 'center' }}
                    >
                      <Box color={theme.colors.accent} mt="-0.5em" fontWeight="bold">
                        Show
                      </Box>
                    </Typography>
                  </Box>
                </Box>
              )}
              {editProfileQuestions[getQuestionIndex(question.question, editProfileQuestions)]?.question ===
                'Show Country' && (
                <Box display="flex" justifyContent={isDevice ? 'center' : 'flex-end'} padding="1em 0 0 0">
                  <Switch
                    color="primary"
                    onChange={(e: any) => {
                      handleChange(getQuestionIndex(question.question, editProfileQuestions), e, false, '', false, true)
                    }}
                    checked={
                      editProfileQuestions[getQuestionIndex(question.question, editProfileQuestions)]?.userAnswer ===
                      'true'
                        ? true
                        : false
                    }
                  />
                  <Typography variant="h6">Show Location</Typography>
                </Box>
              )}
            </>
          )
        }
      }
    })
  }

  return (
    <FadeIn>
      <Box
        display="flex"
        flexDirection="column"
        padding={isDevice ? '0em' : '0em 2em'}
        margin="0"
        justifyContent="center"
        minWidth={isDevice ? '100%' : '600px'}
      >
        {page === 2 && view === 'member' && (
          <Typography variant="h6">
            <Box marginTop="0.5em" fontWeight="bold">
              Tell us about what you do
            </Box>
          </Typography>
        )}
        {page === 2 && view === 'partner' && (
          <Typography variant={isDevice ? 'body1' : 'h6'}>
            <Box m="0.5em 0" fontWeight="bold">
              Tell us about your organization
            </Box>
          </Typography>
        )}
        {getPageComponents()}
      </Box>
    </FadeIn>
  )
}
